import AutorenewIcon from "@mui/icons-material/PostAdd";
import { Button, ButtonProps, globalMessageHandler } from "common";
import { BoatItem } from "features/boats/boatApi";
import useDictionaries from "features/dictionaries/useDictionaries";
import { OptionItem } from "features/options/optionApi";
import { ProgramVariantItem } from "features/program-variants/programVariantApi";
import { LIMIT_TYPES } from "features/schedules/scheduleConstants";
import { useMemo } from "react";

export interface GroupEventDefaultsButtonProps extends ButtonProps {
  onUpdate: (values: any) => void;
  program_ids: number[];
  boat_id: number;
}

export function GroupEventDefaultsButton({
  onUpdate,
  program_ids,
  boat_id,
  ...buttonProps
}: GroupEventDefaultsButtonProps) {
  const { dictionaries, resolveOptions, filterOptions, resolveOption } =
    useDictionaries();

  const { programs, variants, options } = useMemo(() => {
    const programs = resolveOptions("programs", program_ids);
    const variants = filterOptions<ProgramVariantItem>("variants", item =>
      program_ids?.includes(item.program_id),
    );
    const options = filterOptions<OptionItem>("options", item =>
      program_ids?.includes(item.program_id),
    );
    return { programs, variants, options };
  }, [program_ids]);

  function handleClick() {
    globalMessageHandler.confirm(
      {
        title: "Biztos felülírod az esemény adatait?",
        message:
          "A Programtípusok, Limitek, Opciók, Asztaltárképek felülírásra kerülnek.",
      },
      () => {
        onUpdate(getData());
      },
    );
  }

  function getVariants() {
    return variants.map(variant => ({
      variant_id: variant.id,
      adult_price: 1,
      kid_price: 0,
    }));
  }

  function getLimits() {
    const boat = resolveOption<BoatItem>("boats", boat_id);
    const boatCapacity = boat?.capacity || 100;

    const programLimits = programs.map((program: any) => {
      const programVariants = variants.filter(v => v.program_id === program.id);
      return {
        type: LIMIT_TYPES.PROGRAM,
        program_id: program.id,
        variant_ids: programVariants.map(v => v.id),
        number_of_places: boatCapacity,
        number_of_online_places: boatCapacity - 10,
        places_tolerance: Math.ceil(boatCapacity * 0.1),
      };
    });

    return programLimits;
  }

  function getBoatFields() {
    const boat = resolveOption<BoatItem>("boats", boat_id);
    const boatCapacity = boat?.capacity || 100;
    return {
      number_of_places: boatCapacity,
      number_of_online_places: boatCapacity - 10,
      places_tolerance: Math.ceil(boatCapacity * 0.1),
    };
  }

  function getOptions() {
    return options?.map(o => ({ option_id: o.id }));
  }

  function getSeatingLayouts() {
    return programs
      .map((program: any) => ({
        program_id: program.id,
        seating_layout_ids: program?.seating_layout_ids || [],
      }))
      .filter(l => l.seating_layout_ids && l.seating_layout_ids.length > 0);
  }

  function getData() {
    return {
      ...getBoatFields(),
      variants: getVariants(),
      limits: getLimits(),
      options: getOptions(),
      seating_layouts: getSeatingLayouts(),
    };
  }

  return (
    <Button
      startIcon={<AutorenewIcon />}
      title="Kitölt"
      onClick={handleClick}
      {...buttonProps}
    />
  );
}
